import React, { useState, useEffect, FC } from "react";
import { useLocation } from "react-router-dom";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { SettingsProfileImage } from "./pages/profileImage/SettingsProfileImage";
import { GeneralSettings } from "./pages/general/SettingsGeneral";
import { SettingsAccounts } from "./pages/accounts/SettingsAccounts.fetcher";
import { ThemeSwitcher } from "./pages/themes/ThemeSwitcher";
import { refreshUser } from "@/utils/sharedHelperFunctions";
import { brand } from "../../../../../branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { SettingsTabs } from "../SettingsTabs";
import { SettingsPreferences } from "./pages/preferences/SettingsPreferences";
import PreferencesData from "./pages/preferences/PreferencesData";
import { ProfileSettingsTabData } from "../ProfileSettingsTabData";
import { ChangePasswordPage } from "./pages/password/ChangePasswordPage";
import i18next from "i18next";
import { Box, Card, Text, Heading, Tabs } from "@radix-ui/themes";
import useViewportWidth from "@/hooks/useViewportWidth";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export const ProfileSettings: FC = (): JSX.Element => {
  const [currentTab, setCurrentTab] = useState(
    localStorage.getItem("lastCurrentTab") ||
      i18next.t("settings.tabs.yourInfo")
  );

  useEffect(() => {
    setCurrentTab(i18next.t("settings.tabs.yourInfo"));
  }, [i18next.language]);

  const [currentChildTab, setCurrentChildTab] = useState("");
  const isMobile = useViewportWidth(768);

  const currentUser = useCurrentUserProfile((s) => s.user);
  const setUserProfile = useCurrentUserProfile((s) => s.setUser);
  const location = useLocation();

  const prefTabsArray = Object.values(PreferencesData);
  const prefTabsTitle = prefTabsArray.find((tab) => tab.title === currentTab)
    ?.title;
  const activePreferenceTab = prefTabsArray.filter(
    (pref) => pref.title === currentChildTab
  )[0]?.inputs;

  const isDiscordUser = currentUser?.authenticationMethod === "discord";
  const isTwitchUser = currentUser?.authenticationMethod === "twitch";
  const isNotDiscordOrTwitchUser = !isDiscordUser && !isTwitchUser;

  // if hasFeature is false, the set tab will be removed completely and become unaccessible
  const settingsTabs: { [key: string]: JSX.Element | undefined } = {
    [i18next.t("settings.tabs.yourInfo")]: (
      <GeneralSettings setCurrentTab={setCurrentTab} />
    ),
    [i18next.t("settings.tabs.profileImage")]: isNotDiscordOrTwitchUser && (
      <SettingsProfileImage />
    ),
    Connections: brand.hasFeature["connections"] && <SettingsAccounts />,
    [i18next.t("settings.tabs.themes")]: brand.hasFeature["themes"] && (
      <ThemeSwitcher />
    ),
    [i18next.t("settings.tabs.preferences")]: brand.hasFeature[
      "preferences"
    ] && (
      <SettingsPreferences
        setCurrentTab={setCurrentTab}
        setCurrentChildTab={setCurrentChildTab}
      />
    ),
    [i18next.t("settings.tabs.changePassword")]: brand.hasFeature[
      "changePassword"
    ] && <ChangePasswordPage />,
  };

  const tabs = Object.entries(settingsTabs).map(([key, value]) => {
    if (value) {
      return key;
    }
  });

  useEffect(() => {
    refreshUser(setUserProfile, currentUser);
  }, []);

  useEffect(() => {
    if (currentTab && !isMobile) {
      localStorage.setItem("lastCurrentTab", currentTab);
    }
    if (isMobile) {
      localStorage.removeItem("lastCurrentTab");
    }
  }, [currentTab]);

  useEffect(() => {
    if (location.state?.from === "sign-up-step-4") {
      setCurrentTab("Connections");
    }
    if (location.state?.to === "Connections") {
      setCurrentTab("Connections");
    }
    if (location.state?.from === "home") {
      setCurrentTab("Connections");
    }
  }, [location.state?.from]);

  useDocumentTitle(i18next.t("settings.headline"));

  return (
    <Card>
      <Box px="4">
        <Box mb="4">
          <Heading as="h1" mb="0">
            {i18next.t("settings.headline")}
          </Heading>
          <Text size="2">{i18next.t("settings.desc")}</Text>
        </Box>
        <Tabs.Root defaultValue={i18next.t("settings.tabs.yourInfo")}>
          {/* If screen width is bigger than 576px the tabs appear on the left panel */}

          <SettingsTabs
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            setCurrentChildTab={setCurrentChildTab}
            title={i18next.t("settings.tabs.language")}
            tabs={tabs}
          />

          <Box pt="3">
            <Tabs.Content value={currentTab}>
              <ProfileSettingsTabData
                setCurrentChildTab={setCurrentChildTab}
                settingsTabs={settingsTabs}
                currentTab={currentTab}
                activePreferenceTab={activePreferenceTab}
                currentChildTab={currentChildTab}
              />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Box>
    </Card>
  );
};
